import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { PlugZap } from 'lucide-react';

interface ConnectorStatus {
  code: string;
  mode: 'MOCK' | 'REAL' | string;
}

const CONNECTOR_LABELS: Record<string, string> = {
  MYID: 'MyID',
  CLIENT_MANAGER: 'Client Manager',
  CARD_MANAGER: 'Card Manager',
  LOAN_MANAGER: 'Loan Manager',
  INVOICE_SERVICE: 'misc_connectors.l_INVOICE_SERVICE',
  FISCAL: 'misc_connectors.l_FISCAL',
  MERCHANT_PAYOUT: 'misc_connectors.l_MERCHANT_PAYOUT',
  NOTIFICATION: 'misc_connectors.l_NOTIFICATION',
};

export default function Connectors() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({ queryKey: ['/connectors'] });
  const connectors = (data?.data ?? []) as ConnectorStatus[];

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in">
      <h1 className="text-xl font-semibold text-foreground mb-1">{t('misc_connectors.title')}</h1>
      <p className="text-sm text-muted-foreground mb-4">{t('misc_connectors.subtitle')}</p>

      {isLoading ? (
        <p className="text-center text-muted-foreground p-8">{t('misc_common.loading')}</p>
      ) : connectors.length === 0 ? (
        <p className="text-center text-muted-foreground p-8">{t('misc_connectors.empty')}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {connectors.map(c => (
            <div key={c.code} className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <PlugZap size={16} className="text-muted-foreground" />
                  <p className="text-sm font-medium text-foreground">{CONNECTOR_LABELS[c.code] ? t(CONNECTOR_LABELS[c.code]) : c.code}</p>
                </div>
                <Badge variant={c.mode === 'REAL' ? 'default' : 'secondary'} className="text-xs">
                  {c.mode === 'REAL' ? t('misc_connectors.real') : c.mode === 'MOCK' ? t('misc_connectors.mock') : c.mode}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground font-mono">{c.code}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
